// Costanti e utility per la gestione offerte
import { Clock, PhoneCall, XCircle, Archive, CheckCircle, FileSearch, CreditCard, ShieldCheck, FileText, ArrowRight, ThumbsDown, UserCheck, Ban } from 'lucide-react';

// Esiti per Agente (base - prima dell'approvazione)
export const ESITO_OPTIONS_BASE = [
  { value: 'interessato_attesa', label: 'Interessato - In attesa', icon: Clock, color: 'bg-blue-100 text-blue-700' },
  { value: 'richiamare', label: 'Richiamare', icon: PhoneCall, color: 'bg-amber-100 text-amber-700' },
  // NUOVO: Esito "Accettato" per far progredire l'offerta manualmente (Fase 1 - accesso web surgelato)
  { value: 'accettato_agente', label: '✓ Accettato (conferma cliente)', icon: UserCheck, color: 'bg-emerald-100 text-emerald-700' },
  { value: 'richiesta_documentazione', label: 'Richiesta Documentazione', icon: FileSearch, color: 'bg-violet-100 text-violet-700' },
  { value: 'pratica_credito', label: 'Pratica di Credito', icon: CreditCard, color: 'bg-indigo-100 text-indigo-700' },
  { value: 'perso_concorrenza', label: 'Perso - Concorrenza', icon: XCircle, color: 'bg-red-100 text-red-700' },
  { value: 'perso_prezzo', label: 'Perso - Prezzo', icon: XCircle, color: 'bg-red-100 text-red-700' },
  { value: 'non_interessato', label: 'Non interessato', icon: Archive, color: 'bg-slate-100 text-slate-700' }
];

// Esiti per Agente dopo approvazione Admin
export const ESITO_OPTIONS_POST_APPROVAZIONE = [
  { value: 'contratto_da_firmare', label: 'Contratto da Firmare', icon: FileText, color: 'bg-amber-100 text-amber-700' },
  { value: 'contratto_firmato', label: 'Contratto Firmato', icon: CheckCircle, color: 'bg-emerald-100 text-emerald-700' }
];

// Esiti decisivi Admin (solo per pratiche in valutazione)
export const ESITO_OPTIONS_ADMIN = [
  { value: 'approvato', label: 'APPROVATO', icon: ShieldCheck, color: 'bg-emerald-100 text-emerald-700' },
  { value: 'rifiutato', label: 'RIFIUTATO', icon: ThumbsDown, color: 'bg-red-100 text-red-700' }
];

// Esito speciale per Admin: Offerta Non Valida (eliminazione automatica dopo 48h)
export const ESITO_NON_VALIDA = { 
  value: 'offerta_non_valida', 
  label: '⛔ Offerta Non Valida', 
  icon: Ban, 
  color: 'bg-red-200 text-red-800 border-2 border-red-500' 
};

// Opzione rollback (step-back)
export const ESITO_ROLLBACK = [
  { value: 'approvato', label: 'Riporta ad Approvato', icon: ArrowRight, color: 'bg-amber-100 text-amber-700' }
];

// Tutti gli esiti per la visualizzazione badge
export const ALL_ESITI = [
  ...ESITO_OPTIONS_BASE,
  ...ESITO_OPTIONS_POST_APPROVAZIONE,
  { value: 'approvato', label: 'APPROVATO', icon: ShieldCheck, color: 'bg-emerald-100 text-emerald-700' },
  { value: 'rifiutato', label: 'RIFIUTATO', icon: XCircle, color: 'bg-red-100 text-red-700' },
  ESITO_NON_VALIDA  // Esito speciale per offerte non valide
];

// Determina gli esiti disponibili in base allo stato dell'offerta e al ruolo utente
export const getEsitoOptionsForOffer = (offer, userRole = 'agente', isOwnOffer = true) => {
  const currentEsito = offer?.esito;
  const isProspect = offer?.contact_info?.tipo_profilo === 'prospect';
  const isAdmin = ['admin', 'super_admin', 'top_agent'].includes(userRole);
  
  // Offerta già marcata come non valida - nessun cambio permesso
  if (currentEsito === 'offerta_non_valida') {
    return [];
  }
  
  // Fase post-contratto firmato: solo rollback
  if (currentEsito === 'contratto_firmato') {
    return ESITO_ROLLBACK;
  }
  
  // Fase contratto da firmare: può confermare firma o tornare indietro
  if (currentEsito === 'contratto_da_firmare') {
    return [
      { value: 'contratto_firmato', label: 'Contratto Firmato', icon: CheckCircle, color: 'bg-emerald-100 text-emerald-700' },
      ...ESITO_ROLLBACK
    ];
  }
  
  // Fase approvato: agente può avanzare al contratto
  if (currentEsito === 'approvato') {
    return ESITO_OPTIONS_POST_APPROVAZIONE;
  }
  
  // Admin su offerte di altri agenti in fase valutazione (richiesta_documentazione o pratica_credito)
  if (isAdmin && !isOwnOffer && isProspect && ['richiesta_documentazione', 'pratica_credito'].includes(currentEsito)) {
    return ESITO_OPTIONS_ADMIN;
  }
  
  // Caso base: opzioni standard per agenti
  // Admin/Top Agent hanno anche l'opzione "Offerta Non Valida"
  const baseOptions = ESITO_OPTIONS_BASE;
  if (isAdmin) {
    return [...baseOptions, ESITO_NON_VALIDA];
  }
  return baseOptions;
};

// Verifica se l'offerta è in fase di chiusura (dopo approvazione)
export const isInClosingPhase = (offer) => {
  return ['approvato', 'contratto_da_firmare', 'contratto_firmato'].includes(offer?.esito);
};

// Formatta data per visualizzazione
export const formatDate = (dateStr) => {
  try {
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  } catch {
    return dateStr;
  }
};

// Badge esito
export const getEsitoBadge = (esito) => {
  const opt = ALL_ESITI.find(o => o.value === esito);
  return opt ? { label: opt.label, color: opt.color } : { label: 'Da esitare', color: '' };
};

// Accettato, Richiesta documentazione e Pratica di credito = offerta accettata
export const isOffertaAccettata = (offer) =>
  offer.status === 'pending_docs' || ['richiesta_documentazione', 'pratica_credito'].includes(offer.esito);
